/* eslint-disable react/display-name */
import React, { memo } from 'react';
import styled from 'styled-components';

import { smallMediaQuery } from '../../common/utilities/sizeHelpers';
import { reduxConnect } from '../../common';
import { IconColors } from '../common/icons/iconConstants';
import { AirQualityInfoContainer } from './airQualityInfoContainer';
import { getAirQualityDescription } from './getAirQualityDescription';
import { translate } from '../../common/translation/translate';
import { AirQuality, CurrentAirQuality, IStoreState } from '../common/interfaces/types';
import { AirQualityInfoLoadingUI } from './v2/loadingUI/airQualityInfoLoadingUI';
import { aqiNoDataErrorStatusCode } from './constants';
import { NoAqiDataUI } from './v2/noDataUI/noAqiDataUI';
import { DefaultAqiErrorUI } from './v2/noDataUI/defaultAqiErrorUI';
import { convertUnixTimeStampToDate } from '../../common/utilities/dateTimeUtils';

interface AirQualityItemProps {
    currentAirQuality?: CurrentAirQuality;
    aqiIsLoading: boolean;
    lastError: Record<string, unknown>;
    isNoDataError: boolean; 
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { airQuality } = storeState ?? {};
    const { currentAirQuality, currentAirQualityIsFetching, lastError = {} } = airQuality as AirQuality;
    const isNoDataError = lastError?.responseStatus === aqiNoDataErrorStatusCode;

    return {
        currentAirQuality,
        aqiIsLoading: currentAirQualityIsFetching,
        lastError,
        isNoDataError
    };
};

const mapDispatchToProps = {};

const dateFormat = 'h:mm a';
const defaultIndexValue = 0;
const defaultBackgroundColor = IconColors.BackgroundFillColor;

export const AirQualityItem = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({ 
    currentAirQuality,
    lastError,
    isNoDataError,
    aqiIsLoading = false 
}: AirQualityItemProps) => {
    const aqiDataLoadError = !isNoDataError && Object.entries(lastError).length > 0 ? true : false;
    const aqiTodaysConditions = currentAirQuality?.aqi?.todaysConditions || '';
    const responseTime = currentAirQuality?.responseTime  ? convertUnixTimeStampToDate(currentAirQuality.responseTime, dateFormat) : '';
    const responseTimeText = `${translate('lastUpdatedAt')} ${responseTime}`;
    const index = currentAirQuality?.aqi?.index || defaultIndexValue;
    const backgroundFillColor = currentAirQuality?.aqi?.color || defaultBackgroundColor;
    const aqiDescription = getAirQualityDescription(index);

    return (
        <AirQualityAlertContainer>
            {
                aqiIsLoading 
                && <AirQualityInfoLoadingUI />
            }
            {
                aqiDataLoadError && !aqiIsLoading
                && <DefaultAqiErrorUI />
            }
            {
                isNoDataError && !aqiIsLoading
                && <NoAqiDataUI />
            }
            {
                currentAirQuality && !aqiIsLoading && !aqiDataLoadError
                && <AirQualityInfoContainer 
                    backgroundFillColor={backgroundFillColor}
                    index={index.toString()}
                    aqiDescription={aqiDescription}
                    aqiTodaysConditions={aqiTodaysConditions}
                    lastUpdatedTime={responseTimeText}
                />
            }
        </AirQualityAlertContainer>
    );
}));

const AirQualityAlertContainer = styled.div`
    height: 300px;
    overflow-y: scroll;

    @media ${smallMediaQuery} {
        height: 210px;
    }
`;
